import React, { useState, useRef } from 'react';
import Select, { components, SelectInstance, Props as SelectProps } from 'react-select';

// components
import SiteIcon from './site-icon';

// types.
import { Site } from '../utils/types';

// icons
import Trash from '../icons/bin.png';
import light_bin from '../icons/bin-light.256.png';
import dark_bin from '../icons/bin-dark.256.png';

//  --------------------------------------------------------------------------
//
//  P R O P E R T I E S
//
//  --------------------------------------------------------------------------

interface EditableSelectProps extends SelectProps<any> {
  styles: any;
  isDisabled: boolean;
  options: any;
  placeholder?: string;
  value: any;
  onChange: any;
  inputValue?: string;
  onInputChange?: any;
  onDelete?: (id: string | number) => void;
  isClearable?: boolean;
  isSearchable?: boolean;
  controlShouldRenderValue?: boolean;
  gatewayTheme: string;
  showSiteIcon?: boolean;
}

//  --------------------------------------------------------------------------
//
//  H T M L   C U S T O M   C O M P O N E N T S
//
//  --------------------------------------------------------------------------

// use a custom Input component to ensure the input is not hidden in a Select component.
const Input = (props: any) => <components.Input {...props} isHidden={false} />;

//  ------------------------------------------------------------
//
//  Customise the drop-down list items.
//
//  ------------------------------------------------------------

const CustomOption = (props: any) => {
  //
  const {
    data,
    innerRef,
    innerProps,
    selectProps,
    isFocused,
    isSelected,
    gatewayTheme,
    showSiteIcon,
  } = props;
  const onDelete = selectProps.onDelete as ((id: string | number) => void) | undefined;
  const optionStyle =
    selectProps.styles && selectProps.styles.option
      ? selectProps.styles.option({}, { isFocused, isSelected })
      : {};

  // check for a 'highlight' property.
  var highlight: boolean = false;
  if ('highlight' in data) {
    highlight = data['highlight'];
  }

  // get text color.
  var textColour: string = 'black';
  if (highlight === true) {
    if (isFocused === true) {
      if (gatewayTheme === 'dark') {
        textColour = 'yellow';
      } else {
        textColour = '#FF8C00';
      }
    } else {
      if (gatewayTheme === 'dark') {
        textColour = 'yellow';
      } else {
        textColour = '#FF8C00';
      }
    }
  } else {
    if (isFocused === true) {
      if (gatewayTheme === 'dark') {
        textColour = 'white';
      } else {
        textColour = 'white';
      }
    } else {
      if (gatewayTheme === 'dark') {
        textColour = 'white';
      } else {
        textColour = 'black';
      }
    }
  }

  // create a dummy site to hold the icon.
  const site: Site = {
    node: data['label'],
    site: data['label'],
    icon: data['icon'],
  };

  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        ...optionStyle,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        userSelect: 'none',
        padding: '10px',
        backgroundColor:
          isFocused || (isSelected && isFocused == undefined) ? '#0d64bf' : 'transparent',
        transition: '0.0s',
      }}
    >
      {showSiteIcon === true && <SiteIcon site={site} size={24} />}
      <span
        style={{
          color: textColour,
          flexGrow: '1',
          textAlign: 'left',
          margin: '0px 10px 0px ' + (showSiteIcon === true ? '10px' : '0px'),
        }}
      >
        {data.label}
      </span>
      {onDelete && (
        <button
          style={{
            background: 'none',
            border: 'none',
            color: 'red',
            cursor: 'pointer',
            padding: '4px',
          }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent dropdown from closing when clicking delete
            onDelete(data.value);
          }}
        >
          <img
            src={gatewayTheme === 'dark' ? dark_bin : light_bin}
            alt="Delete"
            style={{
              width: '24px',
              height: '24px',
              cursor: 'pointer',
            }}
          />
        </button>
      )}
    </div>
  );
  //
}; // CustomOption

//  --------------------------------------------------------------------------
//
//  C L A S S   D E F I N I T I O N
//
//  --------------------------------------------------------------------------

export default function EditableSelect(props: EditableSelectProps) {
  //
  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  // create reference to the Select component so we can set the focus.
  const selectRef = useRef<SelectInstance | null>(null);

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  const onFocus = () => {
    //
    // ensure that the input component of the Select component does not lose focus when typing.
    if (props.value !== null) {
      selectRef.current?.focus();
    }
    //
  };

  const DropdownIndicator = (args: any) => {
    // create a dummy site to hold the icon.
    const site: Site = {
      node: props.value?.label ?? 'U',
      site: props.value?.label ?? 'U',
      icon: props.value?.icon,
    };
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...args}>
          <SiteIcon site={site} size={24} />
        </components.DropdownIndicator>
      )
    );
  };

  //  --------------------------------------------------------------------------
  //
  //  H O O K S
  //
  //  --------------------------------------------------------------------------

  //  --------------------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  --------------------------------------------------------------------------

  return (
    <Select
      ref={selectRef}
      styles={props.styles}
      isDisabled={props.isDisabled}
      options={props.options}
      isClearable={props.isClearable !== false}
      isSearchable={props.isSearchable !== false}
      defaultValue={props.defaultValue ? props.defaultValue : props.value}
      value={props.value}
      onChange={props.onChange}
      inputValue={props.inputValue}
      placeholder={props.placeholder}
      onInputChange={props.onInputChange}
      onFocus={onFocus}
      menuPosition="fixed"
      controlShouldRenderValue={props.controlShouldRenderValue !== false}
      components={{
        ...{
          Input,
          Option: (customProps) => (
            <CustomOption
              {...customProps}
              gatewayTheme={props.gatewayTheme}
              showSiteIcon={props.showSiteIcon}
            />
          ),
        },
        ...(props.showSiteIcon === true
          ? {
              DropdownIndicator,
            }
          : {}),
      }}
      {...(props.onDelete ? { onDelete: props.onDelete } : {})}
    />
  );
  //
} // EditableSelect()
